import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { Control, Controller, UseFormSetValue, useWatch } from "react-hook-form";
import { useQuery } from "react-query";
import { getAllMasterData, getDistrictMasterData, getStateMasterData } from "../../../../services/master/master_service";

// Every dimension uses this id to mean "All" (no restriction) - matches
// shared/utils/content-targeting.js's ALL_SENTINEL on the backend.
export const ALL_SENTINEL = 0;

export interface MasterOption {
  id: number;
  value: string;
}

const ALL_OPTION: MasterOption = { id: ALL_SENTINEL, value: "All" };

// Some master tables (e.g. state_master) already carry their own "All"
// aggregate row - drop it before prepending our own sentinel option so the
// dropdown doesn't show "All" twice.
function withAllOption(options: MasterOption[]): MasterOption[] {
  const withoutExistingAll = (options || []).filter((option) => option?.value?.trim().toLowerCase() !== "all");
  return [ALL_OPTION, ...withoutExistingAll];
}

// Multi-select dimensions - shared/utils/content-targeting.js's
// SINGLE_SELECT_DIMENSIONS only lists "state", everything else supports
// picking more than one master id. gender/age_group/language are omitted
// entirely: they have no dedicated master table and no known id convention
// anywhere in the backend codebase, so we can't populate or persist them
// correctly yet.
export interface TargetingFormValues {
  state: number;
  district: number[];
  marital_status: number[];
  education: number[];
  working_status: number[];
  occupation: number[];
  type_of_family: number[];
  ownership_of_house: number[];
  durable_ownership: number[];
  personal_income: number[];
  family_income: number[];
}

export const defaultTargeting: TargetingFormValues = {
  state: ALL_SENTINEL,
  district: [ALL_SENTINEL],
  marital_status: [ALL_SENTINEL],
  education: [ALL_SENTINEL],
  working_status: [ALL_SENTINEL],
  occupation: [ALL_SENTINEL],
  type_of_family: [ALL_SENTINEL],
  ownership_of_house: [ALL_SENTINEL],
  durable_ownership: [ALL_SENTINEL],
  personal_income: [ALL_SENTINEL],
  family_income: [ALL_SENTINEL],
};

// Builds the exact `targeting` request body shape the backend expects:
// { [dimension]: number[] }, with `state` (a single-select scalar in the UI)
// wrapped into a one-element array.
export function buildTargetingPayload(values: TargetingFormValues) {
  return {
    state: [values.state ?? ALL_SENTINEL],
    district: values.district?.length ? values.district : [ALL_SENTINEL],
    marital_status: values.marital_status?.length ? values.marital_status : [ALL_SENTINEL],
    education: values.education?.length ? values.education : [ALL_SENTINEL],
    working_status: values.working_status?.length ? values.working_status : [ALL_SENTINEL],
    occupation: values.occupation?.length ? values.occupation : [ALL_SENTINEL],
    type_of_family: values.type_of_family?.length ? values.type_of_family : [ALL_SENTINEL],
    ownership_of_house: values.ownership_of_house?.length ? values.ownership_of_house : [ALL_SENTINEL],
    durable_ownership: values.durable_ownership?.length ? values.durable_ownership : [ALL_SENTINEL],
    personal_income: values.personal_income?.length ? values.personal_income : [ALL_SENTINEL],
    family_income: values.family_income?.length ? values.family_income : [ALL_SENTINEL],
  };
}

// Converts a `{ [dimension]: number[] }` response (from getBannerById /
// getNewsById) back into this form's shape.
export function targetingFromResponse(targeting: Record<string, number[]> | null | undefined): TargetingFormValues {
  return {
    state: targeting?.state?.[0] ?? ALL_SENTINEL,
    district: targeting?.district?.length ? targeting.district : [ALL_SENTINEL],
    marital_status: targeting?.marital_status?.length ? targeting.marital_status : [ALL_SENTINEL],
    education: targeting?.education?.length ? targeting.education : [ALL_SENTINEL],
    working_status: targeting?.working_status?.length ? targeting.working_status : [ALL_SENTINEL],
    occupation: targeting?.occupation?.length ? targeting.occupation : [ALL_SENTINEL],
    type_of_family: targeting?.type_of_family?.length ? targeting.type_of_family : [ALL_SENTINEL],
    ownership_of_house: targeting?.ownership_of_house?.length ? targeting.ownership_of_house : [ALL_SENTINEL],
    durable_ownership: targeting?.durable_ownership?.length ? targeting.durable_ownership : [ALL_SENTINEL],
    personal_income: targeting?.personal_income?.length ? targeting.personal_income : [ALL_SENTINEL],
    family_income: targeting?.family_income?.length ? targeting.family_income : [ALL_SENTINEL],
  };
}

interface TargetingFieldsProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  namePrefix?: string;
  viewMode?: boolean;
}

export default function TargetingFields({ control, setValue, namePrefix = "targeting", viewMode = false }: TargetingFieldsProps) {
  const field = (name: string) => `${namePrefix}.${name}`;

  const { data: allMasterData } = useQuery(["cms-all-master-data"], async () => {
    const response = await getAllMasterData();
    return response?.data?.data || {};
  });

  const { data: states } = useQuery(["cms-state-master"], async () => {
    const response = await getStateMasterData();
    return (response?.data?.data?.list || []) as MasterOption[];
  });

  const stateValue = useWatch({ control, name: field("state") });

  const { data: districts } = useQuery(
    ["cms-district-master", stateValue],
    async () => {
      const response = await getDistrictMasterData(Number(stateValue));
      return (response?.data?.data?.list || []) as MasterOption[];
    },
    { enabled: !!stateValue && stateValue !== ALL_SENTINEL }
  );

  useEffect(() => {
    setValue(field("district"), [ALL_SENTINEL]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateValue]);

  const masterOptions = (key: string): MasterOption[] => withAllOption(allMasterData?.[key] || []);

  const renderSingleSelect = (name: string, label: string, options: MasterOption[], disabled?: boolean) => (
    <Controller
      name={field(name)}
      control={control}
      render={({ field: f }) => (
        <Autocomplete
          fullWidth
          disableClearable
          disabled={viewMode || disabled}
          options={options}
          getOptionLabel={(option) => option?.value || ""}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          value={options.find((item) => item.id === f.value) || ALL_OPTION}
          onChange={(_, newValue) => f.onChange(newValue.id)}
          renderInput={(params) => <TextField {...params} label={label} />}
        />
      )}
    />
  );

  const renderMultiSelect = (name: string, label: string, options: MasterOption[], disabled?: boolean) => (
    <Controller
      name={field(name)}
      control={control}
      render={({ field: f }) => {
        const selectedIds: number[] = f.value?.length ? f.value : [ALL_SENTINEL];
        return (
          <Autocomplete
            multiple
            fullWidth
            disableCloseOnSelect
            disabled={viewMode || disabled}
            options={options}
            getOptionLabel={(option) => option?.value || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            value={options.filter((item) => selectedIds.includes(item.id))}
            onChange={(_, newValue) => {
              let ids = newValue.map((item) => item.id);
              const allJustAdded = ids.includes(ALL_SENTINEL) && !selectedIds.includes(ALL_SENTINEL);
              if (allJustAdded) {
                ids = [ALL_SENTINEL];
              } else {
                ids = ids.filter((id) => id !== ALL_SENTINEL);
                if (!ids.length) ids = [ALL_SENTINEL];
              }
              f.onChange(ids);
            }}
            renderInput={(params) => <TextField {...params} label={label} />}
          />
        );
      }}
    />
  );

  return (
    <Box mt={4}>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        More Fields
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 2 }}>
        {renderSingleSelect("state", "State", withAllOption(states || []))}
        {renderMultiSelect("district", "District", withAllOption(districts || []), !stateValue || stateValue === ALL_SENTINEL)}
        {renderMultiSelect("marital_status", "Marital Status", masterOptions("maritalStatus"))}
        {renderMultiSelect("education", "Education", masterOptions("education"))}
        {renderMultiSelect("working_status", "Working Status", masterOptions("workingStatus"))}
        {renderMultiSelect("occupation", "Occupation", masterOptions("occupation"))}
        {renderMultiSelect("type_of_family", "Type of Family", masterOptions("familyType"))}
        {renderMultiSelect("ownership_of_house", "Ownership of House", masterOptions("houseOwnership"))}
        {renderMultiSelect("durable_ownership", "Durable Ownership", masterOptions("userDurableValues"))}
        {renderMultiSelect("personal_income", "Personal Income", masterOptions("personalIncome"))}
        {renderMultiSelect("family_income", "Family Income", masterOptions("familyIncome"))}
      </Box>
    </Box>
  );
}
